import { useEffect } from 'react'
import {
  useI18nContext,
  type IPP_SUPPORTED_LOCALE,
  LOCALE_STORAGE_KEY,
  isValidLocale,
} from '@retailer-platform/shared-components/src/utils/intl'
import { useUpdateUserLocale } from '../../api/current-account/useUpdateUserLocale.hooks'
import { useCurrentAccountContext } from './CurrentAccountContext'

/**
 * Component that syncs the user's locale from their account data to the I18n context.
 * This component should be rendered inside CurrentAccountProvider to have access to account data.
 *
 * The source of truth for the user's locale is their account's user locale, unless the user has
 * explicitly changed their preferred locale using the locale selector. This logic also handles
 * pre-login locale changes once the user is logged in.
 */
export const AccountLocaleSync = () => {
  const { account } = useCurrentAccountContext()
  const { setLocale } = useI18nContext()
  const { submit: updateUserLocale } = useUpdateUserLocale()

  const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY)
  const userLocale = account?.userLocale?.replace('_', '-') as IPP_SUPPORTED_LOCALE

  useEffect(() => {
    if (storedLocale) {
      // If the stored locale is the same as the user account's locale, do nothing
      if (storedLocale === userLocale) {
        return
      }

      // If the stored locale is different from the user account's locale, update the user's locale
      updateUserLocale({
        userLocale: storedLocale.replace('-', '_') as IPP_SUPPORTED_LOCALE,
      })
      return
    }

    // If the stored locale is not set, set the locale to the user's locale if it is valid
    if (userLocale && isValidLocale(userLocale)) {
      setLocale(userLocale)
    }
  }, [account?.userLocale, setLocale, storedLocale, userLocale, updateUserLocale])

  // This component doesn't render anything
  return null
}
